import React from 'react';
import { motion } from 'framer-motion';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiDollarSign, FiTarget, FiPercent, FiMousePointer, FiStar, FiEye } = FiIcons;

const GoogleAds = ({ client, data }) => {
  // Use passed data or safe defaults
  const safeData = data || {
    roas: 0, costPerConv: 0, convRate: 0, cpc: 0, qualityScore: 0, impressionShare: 0,
    history: [], campaigns: []
  };

  // Ensure history exists for charts
  const historyData = safeData.history && safeData.history.length > 0 ? safeData.history : [
    { month: 'No Data', roas: 0, cost: 0, conversions: 0 }
  ];
  
  const currentMonth = historyData[historyData.length - 1];
  const previousMonth = historyData.length > 1 ? historyData[historyData.length - 2] : currentMonth;

  const calculateChange = (current, previous) => {
    if (!previous) return '0%';
    const change = ((current - previous) / previous * 100).toFixed(1);
    return change > 0 ? `+${change}%` : `${change}%`;
  };

  const metrics = [
    { title: 'ROAS', value: `${safeData.roas}x`, change: calculateChange(safeData.roas, previousMonth.roas), icon: FiDollarSign, color: 'text-green-600', trend: 'up' },
    { title: 'Cost / Conv', value: `$${safeData.costPerConv}`, change: '0%', icon: FiTarget, color: 'text-blue-600', trend: 'down' },
    { title: 'Conv. Rate', value: `${safeData.convRate}%`, change: '0%', icon: FiPercent, color: 'text-purple-600', trend: 'up' },
    { title: 'CPC', value: `$${safeData.cpc}`, change: '0%', icon: FiMousePointer, color: 'text-orange-600', trend: 'down' },
    { title: 'Quality Score', value: safeData.qualityScore, change: '0', icon: FiStar, color: 'text-yellow-600', trend: 'up' },
    { title: 'Impression Share', value: `${safeData.impressionShare}%`, change: '0%', icon: FiEye, color: 'text-indigo-600', trend: 'up' }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-800 mb-2">Google Ads (PPC)</h2>
        <p className="text-gray-600">Pay-per-click advertising performance metrics</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {metrics.map((metric, index) => (
          <motion.div
            key={metric.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <div className={`w-10 h-10 rounded-lg bg-opacity-10 flex items-center justify-center ${metric.color.replace('text-', 'bg-')}`}>
                <SafeIcon icon={metric.icon} className={`text-lg ${metric.color}`} />
              </div>
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-1">{metric.value}</h3>
            <p className="text-sm text-gray-600">{metric.title}</p>
          </motion.div>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
      >
        <h3 className="text-lg font-semibold text-gray-800 mb-4">ROAS & Conversion Trends</h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={historyData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="month" stroke="#6b7280" />
            <YAxis yAxisId="left" stroke="#6b7280" />
            <YAxis yAxisId="right" orientation="right" stroke="#6b7280" />
            <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }} />
            <Line yAxisId="left" type="monotone" dataKey="roas" stroke="#00A859" strokeWidth={3} name="ROAS" />
            <Line yAxisId="right" type="monotone" dataKey="conversions" stroke="#002B60" strokeWidth={3} name="Conversions" />
          </LineChart>
        </ResponsiveContainer>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
      >
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Campaign Performance</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 font-medium text-gray-600">Campaign</th>
                <th className="text-right py-3 px-4 font-medium text-gray-600">Spend</th>
                <th className="text-right py-3 px-4 font-medium text-gray-600">Conversions</th>
                <th className="text-right py-3 px-4 font-medium text-gray-600">ROAS</th>
              </tr>
            </thead>
            <tbody>
              {safeData.campaigns && safeData.campaigns.map((campaign, index) => (
                <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-4 px-4 font-medium text-gray-800">{campaign.name}</td>
                  <td className="py-4 px-4 text-right text-gray-600">${campaign.spend?.toLocaleString()}</td>
                  <td className="py-4 px-4 text-right text-gray-600">{campaign.conversions}</td>
                  <td className="py-4 px-4 text-right font-medium text-gray-800">{campaign.roas}x</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>
    </div>
  );
};

export default GoogleAds;